<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
            <div class="d-flex align-items-center" style="gap: 16px;">
              <div class="header-icon-wrap">
                <i class="fe fe-user" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">{{ auth_user.full_name || 'Enrollee Profile' }}</h4>
                <div class="d-flex align-items-center mt-1" style="gap: 10px;">
                  <small style="opacity: 0.8;">{{ auth_user.id_card_number }}</small>
                  <span v-if="auth_user.status == 'active'" class="header-status--active">
                    <i class="fe fe-check-circle mr-1"></i> Active
                  </span>
                  <span v-else-if="auth_user.status" class="header-status--inactive">
                    <i class="fe fe-alert-circle mr-1"></i> Inactive
                  </span>
                </div>
              </div>
            </div>
            <button class="btn btn-outline-light btn-sm" onclick="history.back()">
              <i class="fe fe-arrow-left mr-1"></i> Back
            </button>
          </div>
        </div>
      </div>

      <section style="margin-top: -32px;">
        <div class="container pb-5">
          <div class="row">

            <!-- ── Photo / Identity Card ── -->
            <div class="col-lg-4 mb-4">
              <div class="card shadow h-100" style="border-radius: 14px; overflow: hidden; border: none;">
                <!-- Gradient top strip -->
                <div style="height: 80px; background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);"></div>

                <div class="card-body text-center pt-0">
                  <!-- Avatar -->
                  <div style="margin-top: -48px;" class="mb-3">
                    <img
                      :src="auth_user.image_url"
                      class="rounded-circle"
                      style="width: 96px; height: 96px; object-fit: cover; border: 4px solid #fff; box-shadow: 0 4px 16px rgba(0,0,0,0.12);"
                      onerror="this.onerror=null; this.src='/claimify_logo.png'"
                    />
                  </div>

                  <h5 class="font-weight-bold mb-1">{{ auth_user.firstname }} {{ auth_user.lastname }}</h5>
                  <p class="text-muted mb-2" style="font-size: 0.82rem;">
                    {{ isDependant ? `Dependant${auth_user.relationship ? ` (${auth_user.relationship})` : ""}` : "Enrollee" }}
                  </p>

                  <div class="mb-3">
                    <span class="code-chip">{{ auth_user.id_card_number }}</span>
                  </div>

                  <span v-if="auth_user.status == 'active'"
                    class="badge px-3 py-2" style="background: #d1fae5; color: #065f46; border-radius: 20px; font-size: 0.78rem;">
                    <i class="fe fe-check-circle mr-1"></i> Active
                  </span>
                  <span v-else-if="auth_user.status"
                    class="badge px-3 py-2" style="background: #fef3c7; color: #92400e; border-radius: 20px; font-size: 0.78rem;">
                    <i class="fe fe-alert-circle mr-1"></i> Inactive
                  </span>

                  <!-- Gender pill -->
                  <div v-if="auth_user.gender" class="mt-3">
                    <span class="badge px-3 py-1" style="background: #eef2ff; color: #1e40af; border-radius: 20px; font-size: 0.78rem;">
                      <i class="fe fe-user mr-1"></i> {{ auth_user.gender }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── Profile Details ── -->
            <div class="col-lg-8 mb-4">
              <div class="card shadow table-card">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-info" style="font-size: 12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Enrollee Details</h6>
                </div>

                <div class="card-body px-4 py-3">
                  <div class="info-row">
                    <span class="info-label"><i class="fe fe-user info-icon"></i> Full Name</span>
                    <span class="info-value">{{ auth_user.firstname }} {{ auth_user.lastname }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label"><i class="fe fe-credit-card info-icon"></i> Enrollee ID</span>
                    <span class="info-value">
                      <span class="code-chip">{{ auth_user.id_card_number }}</span>
                    </span>
                  </div>
                  <div class="info-row">
                    <span class="info-label"><i class="fe fe-phone info-icon"></i> Phone Number</span>
                    <span class="info-value">{{ auth_user.phone_number || '—' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label"><i class="fe fe-gift info-icon"></i> Date of Birth</span>
                    <span class="info-value">{{ auth_user.dob | moment("dddd, MMMM Do YYYY") }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label"><i class="fe fe-user info-icon"></i> Gender</span>
                    <span class="info-value">{{ auth_user.gender || '—' }}</span>
                  </div>
                  <div class="info-row" v-if="auth_user.userprovider">
                    <span class="info-label"><i class="fe fe-home info-icon"></i> Provider</span>
                    <span class="info-value">{{ auth_user.userprovider.agency_name }}</span>
                  </div>
                  <div class="info-row" v-if="auth_user.usertpa">
                    <span class="info-label"><i class="fe fe-shield info-icon"></i> HMO</span>
                    <span class="info-value">{{ auth_user.usertpa.organization_name }}</span>
                  </div>
                  <div class="info-row" v-if="isDependant && auth_user.relationship">
                    <span class="info-label"><i class="fe fe-users info-icon"></i> Relationship</span>
                    <span class="info-value">{{ auth_user.relationship }}</span>
                  </div>
                  <div class="info-row" v-if="auth_user.expiry_date">
                    <span class="info-label"><i class="fe fe-calendar info-icon"></i> Expiry Date</span>
                    <span class="info-value">{{ auth_user.expiry_date | moment("MMM Do YYYY") }}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <!-- ── Encounter history (signed-in users only) ── -->
          <div v-if="!signedIn" class="card shadow table-card">
            <div class="card-body d-flex flex-wrap align-items-center justify-content-between px-4 py-4" style="gap: 12px;">
              <div class="d-flex align-items-center">
                <i class="fe fe-lock text-muted mr-3" style="font-size: 20px;"></i>
                <div>
                  <h6 class="mb-0 font-weight-bold">Encounter history</h6>
                  <small class="text-muted">Health facilities and agency staff can sign in to see this enrollee's encounters and start a new one.</small>
                </div>
              </div>
              <router-link to="/" class="btn btn-submit btn-sm"><i class="fe fe-log-in mr-1"></i> Sign in</router-link>
            </div>
          </div>

          <div v-else class="card shadow table-card">
            <div class="form-card-header d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <div class="section-number"><i class="fe fe-activity" style="font-size: 12px;"></i></div>
                <h6 class="mb-0 font-weight-bold text-white">Encounter history</h6>
              </div>
              <div class="d-flex flex-wrap" style="gap: 8px;">
                <router-link v-if="summary" :to="`/patient/${patientType}/${$route.params.id}`" class="btn btn-outline-light btn-sm">
                  <i class="fe fe-bar-chart-2 mr-1"></i> Full utilization
                </router-link>
                <button v-if="isFacility" class="btn btn-light btn-sm" :disabled="!canStartEncounter" @click="startEncounter">
                  <i class="fe fe-plus-circle mr-1"></i> Start Encounter
                </button>
              </div>
            </div>

            <div class="card-body px-4 py-3">
              <div v-if="isFacility && auth_user && !canStartEncounter" class="alert alert-warning py-2" style="font-size: 0.85rem;">
                <i class="fe fe-alert-triangle mr-1"></i> {{ isDependant ? "This dependant's principal enrollee is not active" : "This enrollee is not active" }}, so an encounter cannot be recorded. The agency must re-enable {{ isDependant ? "the principal" : "them" }} first.
              </div>

              <div v-if="summaryLoading" class="text-muted py-3">Loading encounters…</div>
              <div v-else-if="summaryError" class="text-muted py-3">{{ summaryError }}</div>
              <template v-else-if="summary">
                <div class="row text-center mb-3">
                  <div class="col-4">
                    <div class="summary-figure">{{ summary.total }}</div>
                    <small class="text-muted">Total encounters</small>
                  </div>
                  <div class="col-4">
                    <div class="summary-figure">{{ summary.this_year }}</div>
                    <small class="text-muted">Encounters in {{ summary.year }}</small>
                  </div>
                  <div class="col-4">
                    <div class="summary-figure" style="font-size: 1.05rem; padding-top: 6px;">
                      <template v-if="summary.last_visit">{{ summary.last_visit | moment("MMM D, YYYY") }}</template><template v-else>—</template>
                    </div>
                    <small class="text-muted">Last visit</small>
                  </div>
                </div>

                <h6 class="font-weight-bold mb-2" style="font-size: 0.85rem;">Recent diagnoses</h6>
                <p v-if="!summary.recent.length" class="text-muted mb-0">No encounters recorded yet.</p>
                <div v-for="e in summary.recent" :key="e.id" class="info-row">
                  <span class="info-label" style="min-width: 110px;">{{ e.date | moment("MMM D, YYYY") }}</span>
                  <span class="info-value">
                    {{ e.diagnosis || "No diagnosis recorded" }}<span v-if="e.secondary_diagnosis">, {{ e.secondary_diagnosis }}</span>
                    <small class="d-block text-muted">
                      {{ e.treatment_type || "Encounter" }}<span v-if="e.facility"> · {{ e.facility }}</span>
                    </small>
                  </span>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
        </div>
      </section>

      <!-- ── Webcam Photo Modal (legacy) ── -->
      <div class="modal fade" id="example_01" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div class="modal-content" style="border-radius: 12px; overflow: hidden; border: none;">
            <div class="modal-header" style="background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); border-bottom: none;">
              <h5 class="modal-title text-white font-weight-bold">
                <i class="fe fe-camera mr-2"></i> Capture Photo
              </h5>
              <button type="button" class="close text-white" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body p-4">
              <video id="video" width="100%" height="auto" autoplay class="rounded mb-3"></video>
              <canvas id="canvas" width="720px" height="550px" class="d-none"></canvas>
            </div>
            <div class="modal-footer" style="border-top: 1px solid #e9ecef;">
              <button type="button" class="btn btn-outline-secondary btn-sm" data-dismiss="modal">Close</button>
              <button @click="takePic" class="btn btn-outline-primary btn-sm">
                <i class="fe fe-camera mr-1"></i> Snap Photo
              </button>
              <button type="button" class="btn btn-submit btn-sm" @click="savePic">
                <i class="fe fe-save mr-1"></i> Save Photo
              </button>
            </div>
          </div>
        </div>
      </div>

    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  components: { Navbar, Loading },

  data() {
    return {
      user: null,
      auth_user: "",
      notes: "",
      medications: "",
      image: "",
      edit: false,
      isLoading: false,
      fullPage: true,
      agency_id: "",
      imagefile: "",
      pictureShower: true,
      signedIn: !!localStorage.getItem("jwt") && !!localStorage.getItem("user"),
      summary: null,
      summaryLoading: false,
      summaryError: "",
    };
  },

  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
  },

  computed: {
    // /verify-user/dependant/:id is a dependant; /verify-user/client/:id and the older /verify-user/:id a principal.
    isDependant() {
      return this.$route.params.type === "dependant";
    },
    // The type used by the patient pages and the encounter summary (anything but "client" is a dependant).
    patientType() {
      return this.isDependant ? "dependent" : "client";
    },
    // Encounters are recorded by facilities (the facility account or its staff).
    isFacility() {
      return !!this.user && ["provider", "provider_employee"].includes(this.user.type);
    },
    canStartEncounter() {
      return this.auth_user && Number(this.auth_user.enabled_user) === 1;
    },
  },

  methods: {
    attachPic(event) {
      this.image = event.target.files[0];
      this.uploadPic();
    },

    uploadPic() {
      this.isLoading = true;
      var formData = new FormData();
      formData.append("user_image", this.image);
      formData.append("dependant_id", this.$route.params.id);
      this.axios
        .post("/api/v1/auth/uploadDependantImage", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          console.log(response);
          this.isLoading = false;
          this.$toasted.success("Profile image updated!", { position: "top-center", duration: 3000 });
          this.fetchUser();
        });
    },

    takePic() {
      var video = document.getElementById("video");
      var canvas = document.getElementById("canvas");
      var context = canvas.getContext("2d");
      context.drawImage(video, 0, 0, 640, 480);
      var image = new Image();
      image.src = canvas.toDataURL("image/png");
      this.imagefile = image.src;
    },

    savePic() {
      this.axios
        .post(`/api/v1/auth/uploadcustomerpicImage`, {
          user_image: this.imagefile,
          user_id: this.$route.params.id,
        })
        .then((response) => {
          console.log(response);
          this.$toasted.success("Profile image saved!", { position: "top-center", duration: 3000 });
          this.fetchUser();
        })
        .catch((error) => { console.error(error); });
    },

    fetchSummary() {
      if (!this.signedIn) return;
      this.summaryLoading = true;
      this.summaryError = "";
      this.axios
        .get(`/api/v1/auth/patients/${this.patientType}/${this.$route.params.id}/encounter-summary`)
        .then((response) => { this.summary = response.data.data; })
        .catch((error) => {
          const status = error.response && error.response.status;
          if (status === 401) {
            this.signedIn = false;
          } else {
            this.summaryError = status === 404 || status === 403
              ? "This enrollee's encounters are only visible to their agency and its facilities."
              : "Could not load the encounters.";
          }
        })
        .finally(() => { this.summaryLoading = false; });
    },

    // Opens the encounter page with this enrollee found and selected, as a primary treatment.
    startEncounter() {
      this.$router.push({
        path: "/add-record",
        query: {
          enrollee: this.auth_user.id_card_number || this.auth_user.full_name,
          type: "primary",
          patient: `${this.patientType}:${this.auth_user.id}`,
        },
      });
    },

    fetchUser() {
      const url = this.isDependant ? `/api/v1/dependant-no-auth/${this.$route.params.id}` : `/api/v1/user-no-auth/${this.$route.params.id}`;
      this.axios
        .get(url)
        .then((response) => { this.auth_user = response.data.user; })
        .catch((error) => { console.error(error); });
    },
  },

  created() {
    this.fetchUser();
    this.fetchSummary();
  },
};
</script>

<style scoped>
.summary-figure {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1e3a5f;
  line-height: 1.2;
}
</style>
